import React, { useState, useEffect } from 'react';
import { FiSearch, FiFilter, FiChevronDown, FiX, FiArrowLeft } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import CaseDetailPanel from '../components/CaseDetailPanel';
import { useCase } from '../contexts/CaseContext';
import api from '../utils/api';
import { Heading } from '../components/ui/heading';

// This is an implementation of the UnifiedCaseRegistry with Olivia integration
// It can serve as a new page or replace the existing UnifiedComplaintsRegistry

const UnifiedCaseRegistry: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [cases, setCases] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  
  // Use the CaseContext
  const { 
    setCurrentCaseId, 
    setCaseTitle, 
    setCaseData, 
    oliviaSidebarOpen, 
    toggleOliviaSidebar 
  } = useCase();
  
  // Fetch cases on mount (mock implementation)
  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        // Replace this with real API call if available
        // const result = await api.getPaginatedCases();
        // setCases(result.items);
        
        // For demonstration, using mock data
        setCases(mockCases);
      } catch (error) {
        console.error("Error fetching cases:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCases();
  }, []);
  
  // Handle case selection
  const handleSelectCase = (caseItem: any) => {
    setSelectedCase(caseItem);
    
    // Update CaseContext
    setCurrentCaseId(caseItem.id);
    setCaseTitle(caseItem.title);
    setCaseData(caseItem);
    
    // On mobile, switch to detail view
    if (window.innerWidth < 768) {
      setViewMode('detail');
    }
  };
  
  // Toggle Olivia sidebar
  const handleToggleOlivia = () => {
    toggleOliviaSidebar();
  };
  
  // Back to list view (mobile only)
  const handleBackToList = () => {
    setViewMode('list');
  };
  
  // Filter cases by search term
  const filteredCases = cases.filter(caseItem => 
    caseItem.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caseItem.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caseItem.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caseItem.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caseItem.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Mock data for demonstration
  const mockCases = [
    // Sample case data with same structure as complaint registry
    {
      id: 'CASE-001',
      title: 'Account access issues on mobile app',
      customer: 'John Smith',
      category: 'Consumer Understanding',
      subcategory: 'Digital Services',
      status: 'Open',
      priority: 'High',
      dateReceived: '2025-03-15T10:23:00',
      targetResolutionDate: '2025-03-22T10:23:00',
      source: 'Mobile App',
      description: 'Customer unable to access account through mobile app for 3 days. Multiple attempts resulted in app crashes.',
      customerDetails: {
        phone: '+27 71 123 4567',
        email: 'john.smith@email.com',
        accountNumber: '4532-9871-0023',
        accountType: 'EveryDay Account',
        branch: 'Johannesburg CBD',
      },
      aiInsights: {
        rootCause: 'Server capacity limitations during peak usage times (8-9am and 5-7pm)',
        suggestedActions: [
          'Increase server capacity during peak hours',
          'Implement queue system for high traffic periods',
          'Provide status updates via SMS during outages'
        ],
        similarIssues: 12,
        pattern: 'Geographic concentration in urban areas during business hours',
        riskLevel: 'High - Potential regulatory concern if persistent'
      },
      ncdClassification: {
        principle: 'Consumer Understanding',
        crossCuttingRules: ['Communications'],
        outcome: 'Consumer Support'
      },
      interactions: [
        {
          date: '2025-03-15T10:23:00',
          type: 'Initial Complaint',
          notes: 'Customer submitted complaint through mobile app feedback form'
        },
        {
          date: '2025-03-15T15:45:00',
          type: 'Customer Contact',
          agent: 'Sarah M',
          notes: 'Called customer to gather additional details about the app crashes'
        }
      ]
    },
    {
      id: 'CASE-002',
      title: 'Double charge for monthly subscription',
      customer: 'Michael Brown',
      category: 'Price and Value',
      subcategory: 'Incorrect Charges',
      status: 'Escalated',
      priority: 'High',
      dateReceived: '2025-03-14T09:47:00',
      targetResolutionDate: '2025-03-19T09:47:00',
      source: 'Email',
      description: 'Customer charged twice for monthly subscription fee of R125 and was unable to reach support to resolve the issue.',
      customerDetails: {
        phone: '+27 63 789 0123',
        email: 'michael.b@email.com',
        accountNumber: '6219-5643-8901',
        accountType: 'TFG Money Account',
        branch: 'Durban North',
      },
      aiInsights: {
        rootCause: 'Database synchronization error during scheduled maintenance on March 13',
        suggestedActions: [
          'Implement additional validation checks for recurring charges',
          'Improve maintenance procedures to prevent billing system errors',
          'Create automated detection for duplicate charges'
        ],
        similarIssues: 14,
        pattern: 'Affected customers all subscribed in the same week',
        riskLevel: 'High - Financial impact and regulatory reporting required'
      },
      ncdClassification: {
        principle: 'Price and Value',
        crossCuttingRules: ['Fair Value'],
        outcome: 'Price and Value'
      },
      interactions: [
        {
          date: '2025-03-14T09:47:00',
          type: 'Initial Complaint',
          notes: 'Customer emailed regarding duplicate charge'
        },
        {
          date: '2025-03-14T16:23:00',
          type: 'Internal Investigation',
          agent: 'Accounting Team',
          notes: 'Confirmed system error causing duplicate charges for approx. 15 customers'
        }
      ]
    },
    {
      id: 'CASE-003',
      title: 'Difficulty accessing account for person with disability',
      customer: 'Emma Wilson',
      category: 'Consumer Support',
      subcategory: 'Accessibility',
      status: 'Open',
      priority: 'High',
      dateReceived: '2025-03-13T16:38:00',
      targetResolutionDate: '2025-03-20T16:38:00',
      source: 'Branch Visit',
      description: 'Customer with visual impairment reports significant difficulty using the mobile banking app as it is not properly optimized for screen readers, creating barriers to accessing banking services.',
      customerDetails: {
        phone: '+27 72 345 6789',
        email: 'emma.w@email.com',
        accountNumber: '1290-4367-5423',
        accountType: 'Fixed Deposit Account',
        branch: 'Pretoria East',
      },
      aiInsights: {
        rootCause: 'Mobile app not fully compliant with WCAG 2.1 accessibility standards, particularly for screen reader compatibility',
        suggestedActions: [
          'Prioritize accessibility fixes in next app release',
          'Provide training for customer service staff on supporting customers with disabilities',
          'Create accessibility-specific user testing group'
        ],
        similarIssues: 14,
        pattern: 'Multiple accessibility issues reported by customers with various disabilities',
        riskLevel: 'High - Potential regulatory concern under Outcome 6: No Barriers and equality legislation'
      },
      ncdClassification: {
        principle: 'Consumer Support',
        crossCuttingRules: ['Consumer Service'],
        outcome: 'Consumer Support'
      },
      interactions: [
        {
          date: '2025-03-13T16:38:00',
          type: 'Initial Complaint',
          notes: 'Customer spoke to call center via caregiver to explain difficulties with the mobile app accessibility features. Agent logged formal complaint.'
        },
        {
          date: '2025-03-14T10:15:00',
          type: 'Internal Investigation',
          agent: 'Digital Accessibility Team',
          notes: 'Began assessment of mobile app compatibility with screen readers. Identified several non-compliant interface elements.'
        }
      ]
    }
  ];
  
  // Render different views for mobile
  const isMobile = viewMode === 'detail' && window.innerWidth < 768;
  
  return (
    <div className="h-full flex flex-col">
      {/* Header Section - Hide on mobile detail view */}
      {!isMobile && (
        <>
          <div className="mb-6">
            <Heading level={1} weight="bold" className="text-3xl mb-2">Unified Case Registry</Heading>
            <p className="text-secondary/70">
              View and manage all customer cases with integrated AI insights
            </p>
          </div>
          
          {/* Search and Filter */}
          <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-grow">
                <IconWrapper icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary/50" size={20} />
                <input
                  type="text"
                  placeholder="Search by ID, customer name, category or status..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <button 
                  className="px-4 py-2 rounded-lg text-sm flex items-center bg-white border border-gray-200 text-secondary hover:bg-gray-50 transition-colors"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  <IconWrapper icon={FiFilter} className="mr-2" size={18} />
                  Filters
                  <IconWrapper icon={FiChevronDown} className={`ml-2 transition-transform ${filterOpen ? 'rotate-180' : ''}`} size={16} />
                </button>
              </div>
            </div>
            
            {filterOpen && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                {/* Filter options would go here */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-secondary/70 mb-2">Status</label>
                    <select className="w-full p-2 border border-gray-200 rounded-lg">
                      <option value="">All Statuses</option>
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Escalated">Escalated</option>
                      <option value="Resolved">Resolved</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-secondary/70 mb-2">Priority</label>
                    <select className="w-full p-2 border border-gray-200 rounded-lg">
                      <option value="">All Priorities</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-secondary/70 mb-2">Date Range</label>
                    <div className="flex items-center space-x-2">
                      <input type="date" className="w-full p-2 border border-gray-200 rounded-lg" />
                      <span>to</span>
                      <input type="date" className="w-full p-2 border border-gray-200 rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Mobile Detail View Header */}
      {isMobile && (
        <div className="bg-white p-4 border-b border-gray-200 mb-4 flex items-center">
          <button
            onClick={handleBackToList}
            className="mr-4"
            aria-label="Back to list"
          >
            <IconWrapper icon={FiArrowLeft} size={24} />
          </button>
          <h2 className="text-lg font-bold truncate">{selectedCase?.title || 'Case Details'}</h2>
        </div>
      )}
      
      {/* Main Content - Split View with responsive layout */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-6 overflow-hidden">
        {/* Case List - Hide on mobile detail view */}
        {(!isMobile) && (
          <div className="md:col-span-5 overflow-y-auto bg-white rounded-xl shadow-sm">
            <div className="overflow-x-auto h-full">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">ID</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">Customer</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">Priority</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCases.map((caseItem) => (
                    <tr 
                      key={caseItem.id} 
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedCase?.id === caseItem.id ? 'bg-primary/5' : ''}`}
                      onClick={() => handleSelectCase(caseItem)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary">
                        {caseItem.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                        {caseItem.customer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          caseItem.status === 'Open' ? 'bg-amber-100 text-amber-800' : 
                          caseItem.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 
                          caseItem.status === 'Escalated' ? 'bg-red-100 text-red-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {caseItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          caseItem.priority === 'High' ? 'bg-red-100 text-red-800' : 
                          caseItem.priority === 'Medium' ? 'bg-amber-100 text-amber-800' : 
                          'bg-green-100 text-green-800'
                        }`}>
                          {caseItem.priority}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Case Details with Progressive Disclosure */}
        <div className={`${isMobile ? 'col-span-12' : 'md:col-span-7'} bg-white rounded-xl shadow-sm overflow-y-auto`}>
          {/* We can pass caseData directly or rely on context */}
          <CaseDetailPanel caseData={selectedCase} />
        </div>
      </div>
    </div>
  );
};

export default UnifiedCaseRegistry;